import { computed, type Ref } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { statsApi } from '@/api'
// UserStatsRead type used implicitly
import { POLLING_INTERVALS, STALE_TIME, RETRY_CONFIG } from '@/config/query'
import { assertPersonalSession, personalSession, requirePersonalSession } from '../personal-session'

interface UseUserStatsOptions {
  enabled?: Ref<boolean | undefined>
}

export function useUserStats(options: UseUserStatsOptions = {}) {
  const { enabled } = options
  const queryEnabled = computed(() => Boolean(personalSession.value) && (enabled?.value ?? true))

  const { data, error, isFetching, refetch } = useQuery({
    queryKey: computed(() => ['stats', 'user', personalSession.value?.id ?? null]),
    queryFn: async ({ queryKey }) => {
      const session = requirePersonalSession()
      if (queryKey[2] !== session.id) throw new Error('Wallet session changed.')
      const response = await statsApi.getUserStats()
      assertPersonalSession(session)
      if (response.success && response.data) {
        return { sessionId: session.id, value: response.data }
      }
      throw new Error(response.error ?? 'Failed to fetch user stats')
    },
    refetchInterval: POLLING_INTERVALS.BACKEND_API,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
    retry: RETRY_CONFIG.ATTEMPTS,
    staleTime: STALE_TIME.MEDIUM,
    enabled: queryEnabled,
  })

  return {
    userStats: computed(() =>
      queryEnabled.value && data.value?.sessionId === personalSession.value?.id
        ? data.value?.value
        : undefined
    ),
    isLoading: computed(() => isFetching.value && !data.value),
    hasError: computed(() => Boolean(error.value)),
    errorMessage: computed(() => error.value?.message ?? 'Failed to load user stats'),
    refetch: async () => {
      if (queryEnabled.value) return refetch()
    },
  }
}
